import type { ConnectError } from '@connectrpc/connect'
import { createConnectQueryKey, useMutation } from '@connectrpc/connect-query'
import {
  listPlanHandlingMemos,
  recordPlanHandlingMemo,
} from '@ebalance/proto/bffuser/v1/submission_management_service-SubmissionManagementService_connectquery'
import type { RecordPlanHandlingMemoResponse } from '@ebalance/proto/bffuser/v1/submission_management_service_pb'
import { useQueryClient } from '@tanstack/react-query'

import {
  type PlanTarget,
  toPlanTargetRequest,
} from '~/pages/ProcurementPlanDetail/models/planTarget'

type RecordInput = {
  /** client が生成したメモの id。同じ本文の再試行 (AC-PSM-059) では同じ id を送る。 */
  memoId: string
  /** 前後の空白を除いた本文。 */
  body: string
}

/**
 * 対応メモの記録 mutation hook (AC-PSM-057 / 059)。
 *
 * - 成功: 履歴 (ListPlanHandlingMemos) を再取得する。解消の read
 *   (GetPlanCounterpartyMismatch) は触らない (AC-PSM-058: メモだけでは解消済みにしない)
 * - 失敗: 自動再試行しない (`retry: false`、docs/specs/conventions.md)。表示は呼び出し側
 */
export function useRecordPlanHandlingMemo(target: PlanTarget): {
  record: (
    input: RecordInput,
    callbacks: { onSuccess: () => void; onError: () => void },
  ) => void
  isRecording: boolean
} {
  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation(recordPlanHandlingMemo, {
    retry: false,
    onSuccess: async (_res: RecordPlanHandlingMemoResponse): Promise<void> => {
      await queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listPlanHandlingMemos,
          cardinality: 'finite',
        }),
      })
    },
  })

  return {
    record: (input, callbacks): void => {
      mutate(
        {
          ...toPlanTargetRequest(target),
          memoId: input.memoId,
          body: input.body,
        },
        {
          onSuccess: callbacks.onSuccess,
          onError: (_err: ConnectError): void => {
            callbacks.onError()
          },
        },
      )
    },
    isRecording: isPending,
  }
}
